import { useState, useRef } from 'react';
import { Camera, X, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProfilePicture from '@/components/ProfilePicture';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

/**
 * ProfilePictureUpload component - Handles profile picture upload with preview
 * 
 * @param {string} currentPicture - Current profile picture URL
 * @param {function} onUploadSuccess - Callback when upload is successful
 * @param {boolean} showModal - Whether the upload modal is open
 * @param {function} onClose - Callback to close the modal
 */
const ProfilePictureUpload = ({ 
  currentPicture, 
  onUploadSuccess, 
  showModal, 
  onClose 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_SIZE_MB = 5;
  
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Invalid file type. Please use JPG, PNG, or WebP.');
      return;
    }
    
    // Validate file size
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${MAX_SIZE_MB}MB.`);
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }
    
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API}/users/profile-picture`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Profile picture updated successfully!');
      
      // Update user data in localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.profile_picture = response.data.profile_picture;
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (onUploadSuccess) {
        onUploadSuccess(response.data.profile_picture);
      }
      
      handleClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload profile picture';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!currentPicture) {
      toast.error('No profile picture to delete');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/profile-picture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profile picture removed');
      
      // Update user data in localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      delete userData.profile_picture;
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (onUploadSuccess) {
        onUploadSuccess(null);
      }
      
      handleClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove profile picture';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };
  
  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };
  
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <Dialog open={showModal} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Update Profile Picture
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Current/Preview Picture */}
          <div className="flex flex-col items-center gap-4">
            <ProfilePicture
              src={previewUrl || currentPicture}
              size="2xl"
              alt="Profile preview"
            />
            
            <p className="text-sm text-gray-500 text-center">
              {previewUrl ? 'Preview of new picture' : 'Current picture'}
            </p>
          </div>
          
          {/* File Input (hidden) */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".jpg,.jpeg,.png,.webp"
            className="hidden"
            data-testid="profile-picture-file-input"
          />
          
          {/* File Selection */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={triggerFileSelect}
              className="w-full"
              disabled={uploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {selectedFile ? 'Change Selection' : 'Select Image'}
            </Button>
            
            {selectedFile && (
              <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                <span className="text-sm truncate flex-1">{selectedFile.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          
          {/* Info Text */}
          <p className="text-xs text-gray-500 text-center">
            Accepted formats: JPG, PNG, WebP • Max size: 5MB
            <br />
            Image will be resized to 800×800 pixels
          </p>
          
          {/* Actions */}
          <div className="flex gap-3">
            {currentPicture && !previewUrl && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting || uploading}
                className="flex-1"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove Picture'
                )}
              </Button>
            )}
            
            <Button
              type="button"
              onClick={handleUpload}
              data-testid="profile-picture-save-btn"
              disabled={!selectedFile || uploading}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Save Picture'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfilePictureUpload;
